import type { ShopperProductsTypes } from 'commerce-sdk-isomorphic'

type ProductImpression = {
  id: string
  name: string
  price: number | undefined
  category?: string
  variant: string
  sku: string
  originalPrice: number | undefined
  memberPrice: number | undefined
  brand: string
  list: string
}

export type ProductImpressionPayload = {
  group: string
  category?: string
  label?: string
  info?: {
    location: string
    count: number
  }
  data?: any
  ecommerceType: string
  ecommerce: {
    currencyCode: string
    impressions: ProductImpression[]
  }
}

type ProductImpressionPayloadWithoutImpressions = Omit<ProductImpressionPayload, 'ecommerce'> & {
  ecommerce: Omit<ProductImpressionPayload['ecommerce'], 'impressions'>
}

const impressionMaps: Record<string, ProductImpressionPayload> = {}

const removeImpressionsFromPayload = (
  payload: ProductImpressionPayload,
): ProductImpressionPayloadWithoutImpressions => ({
  ...payload,
  ecommerce: {
    currencyCode: payload.ecommerce.currencyCode,
  },
})

let productImpressionsTimer: ReturnType<typeof setTimeout> | null = null

const rememberedProductImpressions: any[] = []

const productHasNotBeenIncluded = (payload: ProductImpressionPayload) => {
  return !rememberedProductImpressions.includes(payload.ecommerce.impressions[0]?.sku)
}

const rememberProduct = (payload: ProductImpressionPayload) => {
  rememberedProductImpressions.push(payload.ecommerce.impressions[0]?.sku)
}

const pushImpressions = (i18nLocale: Locales) => {
  Object.entries(impressionMaps).forEach(([key, payload]) => {
    pushEventToDataLayer('custom.product.list.view', payload, i18nLocale)
    delete impressionMaps[key]
  })
}

export const createProductImpression = (
  product: ShopperProductsTypes.Product | { id: string },
  list: string,
): ProductImpression => {
  if ('price' in product) {
    const variation = product.variationValues ? Object.values(product.variationValues).join('|') : ''
    const mappedPrices = mapPrices(product.tieredPrices ?? [])
    const { retailPrice, memberPrice } = mappedPrices || {}

    return {
      id: product.id,
      name: product.name || '',
      price: product.price,
      variant: variation,
      sku: product.id,
      originalPrice: retailPrice,
      memberPrice: memberPrice,
      brand: product.brand || '',
      list,
    }
  } else {
    return {
      id: product.id,
      name: '',
      price: undefined,
      variant: '',
      sku: product.id,
      originalPrice: undefined,
      memberPrice: undefined,
      brand: '',
      list,
    }
  }
}

export const useProductImpressions = (i18nLocale: Locales) => {
  const trackImpression = (payload: any) => {
    try {
      const payloadWithoutImpressions = removeImpressionsFromPayload(payload)
      const key = JSON.stringify(payloadWithoutImpressions)
      const combinedPayload = impressionMaps[key]
      if (combinedPayload && productHasNotBeenIncluded(payload)) {
        rememberProduct(payload)
        combinedPayload.ecommerce.impressions.push(...payload.ecommerce.impressions)
      } else if (productHasNotBeenIncluded(payload)) {
        rememberProduct(payload)
        impressionMaps[key] = payload
      }
      if (productImpressionsTimer) {
        clearTimeout(productImpressionsTimer)
        productImpressionsTimer = null
      }
      productImpressionsTimer = setTimeout(() => pushImpressions(i18nLocale), 100)
    } catch (err) {
      console.error(`useProductImpressions/trackImpression`, err)
    }
  }

  return {
    trackImpression,
  }
}
